import { isString } from "@vuepress/helper/client";
import { useEventListener } from "@vueuse/core";
import type { MaybeRef, Ref, ShallowRef } from "vue";
import { computed, isRef, onMounted, ref, shallowRef, unref, watch } from "vue";

import { DEFAULT_RATIO } from "../utils/index.js";

const getValue = (value: string | number): string => (isString(value) ? value : `${value}px`);

export interface SizeOptions {
  width: string | number | undefined;
  height: string | number | undefined;
  ratio: string | number | undefined;
}

export interface SizeInfo<Element extends HTMLElement> {
  el: ShallowRef<Element | undefined>;
  width: Ref<string>;
  height: Ref<string>;
  resize: () => void;
}

export const useSize = <Element extends HTMLElement>(
  options: SizeOptions,
  extraHeight: MaybeRef<number> = 0,
): SizeInfo<Element> => {
  const el = shallowRef<Element>();
  const width = computed(() => getValue(unref(options.width) ?? "100%"));
  const height = ref("auto");

  const getRadio = (ratio: number | string | undefined): number => {
    if (isString(ratio)) {
      const [widthValue, heightValue] = ratio.split(":");
      const parsedRadio = Number(widthValue) / Number(heightValue);

      if (!Number.isNaN(parsedRadio)) return parsedRadio;
    }

    return typeof ratio === "number" ? ratio : DEFAULT_RATIO;
  };

  const getHeight = (widthValue: number): string => {
    const heightValue = unref(options.height);
    const ratio = getRadio(unref(options.ratio));

    return heightValue ? getValue(heightValue) : `${widthValue / ratio + unref(extraHeight)}px`;
  };

  const updateHeight = (): void => {
    if (el.value) height.value = getHeight(el.value.clientWidth);
  };

  onMounted(() => {
    updateHeight();
    if (isRef(extraHeight)) watch(extraHeight, updateHeight);
    useEventListener("orientationchange", updateHeight);
    useEventListener("resize", updateHeight);
  });

  return {
    el,
    width,
    height,
    resize: updateHeight,
  };
};
